// ============================================================
// Session du panel : quand la renouveler, et comment lire un 401
// ============================================================
// Fonctions pures, sans React ni accès réseau ni stockage, testées par sessionPolicy.test.ts
// (node --test). Le reste (appel, stockage, onglets) vit dans session.ts.

/** Dates d'un jeton du panel (secondes epoch). `authTime` : connexion Recube d'origine. */
export interface SessionClaims {
  iat: number;
  exp: number;
  authTime: number;
  sid: string | null;
}

/** Un jeton de plus d'une heure se renouvelle au prochain passage, si la personne travaille. */
export const REFRESH_AFTER_S = 3600;
/** Moins d'un quart d'heure avant l'échéance : renouvellement dès que possible. */
export const REFRESH_BEFORE_EXPIRY_S = 15 * 60;
/** « La personne travaille » : une action (clic, touche, molette, retour sur l'onglet) dans la dernière demi-heure. */
export const ACTIVE_WINDOW_MS = 30 * 60 * 1000;
/** Après un échec (réseau, API en redémarrage), pas de nouvel essai avant 2 minutes. */
export const RETRY_AFTER_FAILURE_MS = 2 * 60 * 1000;

/** Motifs de refus de session envoyés par l'API (champ `code`, cf PanelSessionTokens). */
export const SESSION_CODES = {
  expired: 'SESSION_EXPIRED',
  invalid: 'TOKEN_INVALID',
  missing: 'TOKEN_MISSING',
  closed: 'SESSION_CLOSED',
  maxAge: 'SESSION_MAX_AGE',
} as const;

/**
 * Pourquoi la session ne vaut plus rien, pour le message à la personne :
 * - `expired` : jeton échu (le cas courant, après 24 h sans activité) ;
 * - `max-age` : session arrivée à sa durée maximale depuis la connexion Recube ;
 * - `invalid` : jeton refusé (signature, session fermée).
 */
export type SessionEnd = 'expired' | 'max-age' | 'invalid';

/** Lit les dates d'un jeton (sans vérifier la signature : c'est le travail de l'API), null si illisible. */
export function decodeClaims(token: string | null | undefined): SessionClaims | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const p = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    if (typeof p.exp !== 'number') return null;
    const iat = typeof p.iat === 'number' ? p.iat : p.exp - 24 * 3600;
    return {
      iat,
      exp: p.exp,
      authTime: typeof p.auth_time === 'number' ? p.auth_time : iat,
      sid: typeof p.sid === 'string' ? p.sid : null,
    };
  } catch {
    return null;
  }
}

/** Le jeton est-il assez vieux, ou assez près de son échéance, pour être renouvelé ? */
export function refreshDue(c: SessionClaims, nowS: number): boolean {
  return nowS - c.iat >= REFRESH_AFTER_S || c.exp - nowS <= REFRESH_BEFORE_EXPIRY_S;
}

/**
 * Renouveler maintenant ? Seulement si l'onglet est affiché, que la personne y a agi récemment,
 * que le jeton est dû, et pas juste après un échec. Un jeton déjà échu côté navigateur est quand
 * même tenté : l'horloge du poste peut avancer, c'est l'API qui tranche (et dit « session expirée »).
 */
export function shouldRefresh(o: {
  claims: SessionClaims | null;
  nowMs: number;
  lastActivityMs: number;
  visible: boolean;
  lastFailureMs: number | null;
}): boolean {
  if (!o.claims || !o.visible) return false;
  if (o.nowMs - o.lastActivityMs > ACTIVE_WINDOW_MS) return false;
  if (o.lastFailureMs !== null && o.nowMs - o.lastFailureMs < RETRY_AFTER_FAILURE_MS) return false;
  return refreshDue(o.claims, Math.floor(o.nowMs / 1000));
}

/**
 * Lecture d'un 401 de l'API du panel :
 * - `logout` : plus de jeton du tout (déconnexion dans un autre onglet) ;
 * - `end` : la session est finie, avec son motif : on le dit et on propose de se reconnecter, sans
 *   rien jeter de la page ;
 * - `rejected` : jeton encore bon, refusé sans motif de session : souci côté API (bandeau technique).
 *
 * @param tokenExpired échéance du jeton passée selon l'horloge du navigateur (API sans motif)
 */
export function readUnauthorized(code: string | undefined, hasToken: boolean, tokenExpired: boolean):
  { kind: 'logout' } | { kind: 'end'; end: SessionEnd } | { kind: 'rejected' } {
  if (!hasToken || code === SESSION_CODES.missing) return { kind: 'logout' };
  if (code === SESSION_CODES.expired) return { kind: 'end', end: 'expired' };
  if (code === SESSION_CODES.maxAge) return { kind: 'end', end: 'max-age' };
  if (code === SESSION_CODES.invalid || code === SESSION_CODES.closed) return { kind: 'end', end: 'invalid' };
  if (tokenExpired) return { kind: 'end', end: 'expired' };
  return { kind: 'rejected' };
}

/** Phrase affichée quand la session est finie : les modifications restent sur le serveur. */
export function sessionEndMessage(end: SessionEnd): string {
  if (end === 'max-age') return 'Ta session a atteint sa durée maximale, reconnecte-toi : tes modifications sont gardées.';
  if (end === 'invalid') return "Ta session n'est plus valide, reconnecte-toi : tes modifications sont gardées.";
  return 'Ta session a expiré, reconnecte-toi : tes modifications sont gardées.';
}

/**
 * Erreur rendue par un appel refusé pour fin de session (affichée par les pages, ex. une ligne
 * d'« Envoyer ») : l'API refuse avant de rien faire, la demande peut être refaite telle quelle.
 */
export const SESSION_ENDED_ERROR = "Session expirée : rien n'a été fait. Reconnecte-toi, tes modifications sont gardées.";
